import { BaseService } from './base';
import { ApiRequest } from '../lib/request';
import type { ApiResponse, ApiListResponse } from '../lib/response';
import {
  Customer,
  CustomerCombinedNote,
  CustomerPaymentMethods,
  CustomerCardsPasses,
  CustomerCreditTransaction,
  LoyaltyTransaction,
  ProductHistory,
} from '../models/customer';
import { CustomerCreditCard } from '../models/customerCreditCard';
import { CustomerPass } from '../models/customerPass';
import { CustomerForm } from '../models/customerForm';
import { CustomerCourse } from '../models/course';
import { GiftCard } from '../models/giftCard';
import { Order } from '../models/order';
import { Photo } from '../models/photo';
import { Upload } from '../models/upload';
import { Reservation } from '../models/reservation';
import { Waitlist } from '../models/waitlist';

export class CustomerService extends BaseService<Customer> {
  override model = Customer;

  override modelName = 'customer';
  override modelLabel = 'Customers';

  // Customer rows are small — 500/page cuts the number of round-trips by 5×
  // for any location with >100 customers, without bloating response size.
  override listAllPageSize = 500;

  // Static API Methods

  async filter(filters: any[]): Promise<any> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/filter`,
      data: { filters },
    });

    const response = await this.request(request);
    return response.data ?? response;
  }

  async checkDuplicate({
    page = 1,
    limit = 20,
    filters = {},
  }: {
    page?: number;
    limit?: number;
    filters?: Record<string, any>;
  } = {}): Promise<ApiListResponse<Customer>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/list`,
      data: filters,
      query: {
        page: page.toString(),
        limit: limit.toString(),
      },
    });

    return await this.requestList<Customer>(request, Customer);
  }

  async bulkArchive(
    customerIds: number[],
    { restore = false }: { restore?: boolean } = {}
  ): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/bulkArchive`,
      data: { customers: customerIds },
      query: { restore: restore ? '1' : '0' },
    });

    return await this.request(request);
  }

  async updateSegments({
    customerIds,
    addSegmentIds,
    removeSegmentIds,
  }: {
    customerIds: number[];
    addSegmentIds: number[];
    removeSegmentIds: number[];
  }): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/updateSegments`,
      data: {
        customers: customerIds,
        add: addSegmentIds,
        remove: removeSegmentIds,
      },
    });

    return await this.request(request);
  }

  // Instance-style API Methods (take customer ID)

  async erase(customerId: string): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${customerId}/erase`,
    });

    return await this.request(request);
  }

  async reservations(
    customerId: string,
    { page = 1, limit = 50 }: { page?: number; limit?: number } = {}
  ): Promise<ApiListResponse<Reservation>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${customerId}/reservations`,
      query: { page: page.toString(), limit: limit.toString() },
    });

    return await this.requestList(request, Reservation);
  }

  async upcomingReservations(
    customerId: string,
    { page = 1, limit = 50 }: { page?: number; limit?: number } = {}
  ): Promise<ApiListResponse<Reservation>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${customerId}/upcomingReservations`,
      query: { page: page.toString(), limit: limit.toString() },
    });

    return await this.requestList(request, Reservation);
  }

  async pastReservations(
    customerId: string,
    { page = 1 }: { page?: number } = {}
  ): Promise<ApiListResponse<Reservation>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${customerId}/pastReservations`,
      query: { page: page.toString() },
    });

    return await this.requestList(request, Reservation);
  }

  async unpaidReservations(customerId: string): Promise<ApiListResponse<Reservation>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${customerId}/unpaidReservations`,
    });

    return await this.requestList(request, Reservation);
  }

  async waitlists(
    customerId: string,
    { page = 1 }: { page?: number } = {}
  ): Promise<ApiListResponse<Waitlist>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${customerId}/waitlists`,
      query: { page: page.toString() },
    });

    return await this.requestList<Waitlist>(request, Waitlist);
  }

  async combinedNotes(
    customerId: string,
    { page = 1, type = null }: { page?: number; type?: string | null } = {}
  ): Promise<ApiListResponse<CustomerCombinedNote>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${customerId}/combinedNotes`,
      query: {
        page: page.toString(),
        ...(type ? { type } : {}),
      },
    });

    return await this.requestList<CustomerCombinedNote>(request, CustomerCombinedNote);
  }

  async orders(
    customerId: string,
    { page = 1 }: { page?: number } = {}
  ): Promise<ApiListResponse<Order>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${customerId}/orders`,
      query: { page: page.toString() },
    });

    return await this.requestList(request, Order);
  }

  async forms(
    customerId: string,
    { page = 1, limit = 10, includeItems = false }: { page?: number; limit?: number; includeItems?: boolean } = {}
  ): Promise<ApiListResponse<CustomerForm>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${customerId}/forms`,
      query: {
        page: page.toString(),
        limit: limit.toString(),
        includeItems: includeItems ? '1' : '0',
      },
    });

    return await this.requestList(request, CustomerForm);
  }

  async cardsPasses(customerId: string): Promise<ApiResponse<CustomerCardsPasses>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${customerId}/cardsPasses`,
    });

    return await this.requestItem<CustomerCardsPasses>(request, CustomerCardsPasses);
  }

  async passes(
    customerId: string,
    { unpaid = false, page = 1 }: { unpaid?: boolean; page?: number } = {}
  ): Promise<ApiListResponse<CustomerPass>> {
    const query: Record<string, string> = { page: page.toString() };
    if (unpaid) query.filter = 'unpaid';

    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${customerId}/passes`,
      query,
    });

    return await this.requestList(request, CustomerPass);
  }

  async courses(
    customerId: string,
    { page = 1, limit = 10, unpaid = false }: { page?: number; limit?: number; unpaid?: boolean } = {}
  ): Promise<ApiListResponse<CustomerCourse>> {
    const query: Record<string, string> = { page: page.toString(), limit: limit.toString() };
    if (unpaid) query.filter = 'unpaid';

    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${customerId}/courses`,
      query,
    });

    return await this.requestList(request, CustomerCourse);
  }

  async giftCards(
    customerId: string,
    { unpaid = false }: { unpaid?: boolean } = {}
  ): Promise<ApiListResponse<GiftCard>> {
    const query: Record<string, string> = {};
    if (unpaid) query.filter = 'unpaid';

    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${customerId}/giftCards`,
      query,
    });

    return await this.requestList(request, GiftCard);
  }

  async creditCards(customerId: string): Promise<ApiListResponse<CustomerCreditCard>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${customerId}/creditCards`,
    });

    return await this.requestList<CustomerCreditCard>(request, CustomerCreditCard);
  }

  async creditTransactions(
    customerId: string,
    { page = 1 }: { page?: number } = {}
  ): Promise<ApiListResponse<CustomerCreditTransaction>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${customerId}/creditTransactions`,
      query: { page: page.toString() },
    });

    return await this.requestList<CustomerCreditTransaction>(request, CustomerCreditTransaction);
  }

  async loyaltyTransactions(
    customerId: string,
    { page = 1 }: { page?: number } = {}
  ): Promise<ApiListResponse<LoyaltyTransaction>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${customerId}/loyaltyTransactions`,
      query: { page: page.toString() },
    });

    return await this.requestList<LoyaltyTransaction>(request, LoyaltyTransaction);
  }

  async updateLoyaltyPointBalance(
    customerId: string,
    loyaltyPointBalance: number
  ): Promise<ApiResponse<Customer>> {
    const request = new ApiRequest({
      method: 'PUT',
      url: `${this.modelBaseUrl}/${customerId}`,
      data: { loyaltyPointBalance },
    });

    return await this.requestItem<Customer>(request, Customer);
  }

  async recalculateLoyaltyPointBalance(customerId: string): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${customerId}/recalculateLoyaltyPoints`,
    });

    return await this.request(request);
  }

  async updateCreditBalance(
    customerId: string,
    creditBalance: number
  ): Promise<ApiResponse<Customer>> {
    const request = new ApiRequest({
      method: 'PUT',
      url: `${this.modelBaseUrl}/${customerId}`,
      data: { creditBalance },
    });

    return await this.requestItem<Customer>(request, Customer);
  }

  async productHistory(customerId: string): Promise<ApiListResponse<ProductHistory>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${customerId}/productHistory`,
    });

    return await this.requestList<ProductHistory>(request, ProductHistory);
  }

  async paymentMethods(customerId: string): Promise<ApiResponse<CustomerPaymentMethods>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${customerId}/paymentMethods`,
    });

    return await this.requestItem<CustomerPaymentMethods>(request, CustomerPaymentMethods);
  }

  async uploads(
    customerId: string,
    { page = 1 }: { page?: number } = {}
  ): Promise<ApiListResponse<Upload>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${customerId}/uploads`,
      query: { page: page.toString() },
    });

    return await this.requestList<Upload>(request, Upload);
  }

  async getPhotos(customerId: string): Promise<ApiListResponse<Photo>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${customerId}/photos`,
    });

    return await this.requestList<Photo>(request, Photo);
  }

  async addPhoto(
    customerId: string,
    { photo, caption }: { photo: File | Blob; caption: string },
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<ApiResponse<Photo>> {
    const formData = new FormData();
    formData.append('photo', photo);
    formData.append('caption', caption);

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${customerId}/addPhoto`,
      data: formData,
      onUploadProgress,
    });

    return await this.requestItem<Photo>(request, Photo);
  }

  async deletePhoto(photoId: string): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${photoId}/deletePhoto`,
    });

    return await this.request(request);
  }

  async editPhoto(photo: Photo): Promise<ApiResponse<Photo>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${photo.objectID()}/editPhoto`,
      data: photo.toJSON(),
    });

    return await this.requestItem<Photo>(request, Photo);
  }

  async setProfilePhoto(customerId: string, photoId: string): Promise<ApiResponse<Customer>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${customerId}/setProfilePhoto`,
      query: { photo: photoId },
    });

    return await this.requestItem<Customer>(request, Customer);
  }

  async removeProfilePhoto(customerId: string): Promise<ApiResponse<Customer>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${customerId}/removeProfilePhoto`,
    });

    return await this.requestItem<Customer>(request, Customer);
  }

  async merge(customerId: string, mergeCustomerIds: number[]): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${customerId}/merge`,
      data: { customers: mergeCustomerIds },
    });

    return await this.request(request);
  }

  async deleteCreditCard(creditCardId: string): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/deleteCreditCard`,
      query: { id: creditCardId },
    });

    return await this.request(request);
  }

  async resendWelcomeEmail(customerId: string): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${customerId}/resendWelcomeEmail`,
    });

    return await this.request(request);
  }

  async unblockEmail(customerId: string): Promise<ApiResponse<Customer>> {
    const request = new ApiRequest({ method: 'POST', url: `${this.modelBaseUrl}/${customerId}/unblockEmail` });
    return await this.requestItem(request, Customer);
  }

  async resetPassword(
    customerId: string,
    { password, confirm, sendEmail }: { password: string; confirm: string; sendEmail: boolean }
  ): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${customerId}/resetPassword`,
      query: {
        password,
        confirm,
        sendEmail: sendEmail ? '1' : '0',
      },
    });

    return await this.request(request);
  }

  async exportData(customerId: string): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${customerId}/exportData`,
    });

    return await this.request(request);
  }

  async archive(customerId: string): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${customerId}/archive`,
    });

    return await this.request(request);
  }

  async verifyAge(
    customerId: string,
    ageVerifiedDetail: Record<string, any>
  ): Promise<ApiResponse<Customer>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${customerId}/verifyAge`,
      data: ageVerifiedDetail,
    });

    return await this.requestItem<Customer>(request, Customer);
  }
}
